import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { useAuth } from '@/stores/auth';
import { Navigate, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

export default function ForceChangePasswordPage() {
  const { user, isAuthenticated, isLoading, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState('');
  const { i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  if (isLoading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) return <Navigate to="/login" />;
  if (!user?.mustChangePassword) return <Navigate to="/dashboard" />;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch('/api/auth/force-change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ newPassword }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Password changed successfully!');
        await refreshUser();
        navigate('/dashboard');
      } else {
        toast.error(data.error || 'Failed to change password');
      }
    } catch (err) {
      toast.error('Failed to change password');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #FAF8F4 0%, #F0EDE6 100%)',
      padding: 20,
    }}>
      <div style={{
        background: '#fff',
        borderRadius: 16,
        padding: 40,
        maxWidth: 440,
        width: '100%',
        boxShadow: '0 8px 32px rgba(60,54,50,0.1)',
      }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{
            width: 56, height: 56, borderRadius: '50%',
            background: '#FFF3E0', display: 'flex', alignItems: 'center',
            justifyContent: 'center', margin: '0 auto 16px',
            fontSize: 28,
          }}>
            🔐
          </div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: '#3C3632', marginBottom: 8 }}>
            Password Change Required
          </h1>
          <p style={{ fontSize: 14, color: '#8B8178' }}>
            Your password has been reset by an administrator. Please set a new password to continue.
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#3C3632', marginBottom: 6 }}>
              New Password
            </label>
            <input
              type={showPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password (min 8 characters)"
              required
              minLength={8}
              style={{
                width: '100%', padding: '10px 14px', borderRadius: 8,
                border: '1px solid #E0DCD6', fontSize: 14,
                outline: 'none', boxSizing: 'border-box',
              }}
            />
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#3C3632', marginBottom: 6 }}>
              Confirm New Password
            </label>
            <input
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              required
              minLength={8}
              style={{
                width: '100%', padding: '10px 14px', borderRadius: 8,
                border: '1px solid #E0DCD6', fontSize: 14,
                outline: 'none', boxSizing: 'border-box',
              }}
            />
          </div>

          <div style={{ marginBottom: 24 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#8B8178', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={showPassword}
                onChange={(e) => setShowPassword(e.target.checked)}
                style={{ accentColor: '#B8943A' }}
              />
              Show passwords
            </label>
          </div>

          <button
            type="submit"
            disabled={submitting}
            style={{
              width: '100%', padding: '12px 0', borderRadius: 10,
              background: submitting ? '#CCC' : 'linear-gradient(135deg, #B8943A, #D4A843)',
              color: '#fff', fontWeight: 600, fontSize: 15,
              border: 'none', cursor: submitting ? 'default' : 'pointer',
              boxShadow: '0 4px 12px rgba(184,148,58,0.3)',
            }}
          >
            {submitting ? 'Changing Password...' : 'Set New Password'}
          </button>
        </form>
      </div>
    </div>
  );
}
